import { useState, useEffect } from 'react'
import API from '../services/api'

export function useEvidence(controlId, assetId) {
  const [evidence, setEvidence] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setEvidence([])
    if (controlId && assetId) fetchEvidence()
    else setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlId, assetId])

  async function fetchEvidence() {
    setLoading(true)
    try {
      const { data } = await API.get(`/evidence/control/${controlId}/`, { params: { asset_id: assetId } })
      setEvidence(data.map(row => ({ ...row, profiles: { full_name: row.uploaded_by_name } })))
      setError(null)
    } catch (err) { setError(err?.response?.data?.detail || err.message) }
    finally { setLoading(false) }
  }

  async function createEvidence(control, values, file = null) {
    let payload = { ...values, asset_id: assetId }
    if (file) {
      payload = new FormData()
      Object.entries({ ...values, asset_id: assetId }).forEach(([key, value]) => {
        if (value != null) payload.append(key, value)
      })
      payload.append('file', file)
    }
    await API.post(`/evidence/control/${control}/`, payload)
    await fetchEvidence()
  }

  // Existing UI signatures are retained; identity/tenant arguments are not sent.
  const uploadFileEvidence = (file, control, org, user, title, expiry) =>
    createEvidence(control, { type: 'file', title, expiry_date: expiry || '' }, file)
  const uploadLinkEvidence = (url, control, org, user, title, expiry) =>
    createEvidence(control, { type: 'link', title, url, expiry_date: expiry || null })
  const uploadNoteEvidence = (note, control, org, user, title, expiry) =>
    createEvidence(control, { type: 'note', title, note, expiry_date: expiry || null })

  async function deleteEvidence(id) {
    await API.delete(`/evidence/${id}/`, { params: { asset_id: assetId } })
    await fetchEvidence()
  }

  async function downloadEvidence(id) {
    const row = evidence.find(item => item.id === id)
    if (!row) throw new Error('Evidence record could not be found')
    const { data } = await API.get(`/evidence/${row.id}/download/`, {
      params: { asset_id: assetId }, responseType: 'blob',
    })
    // This URL contains bytes already authorized/downloaded through Django.
    const url = URL.createObjectURL(data)
    setTimeout(() => URL.revokeObjectURL(url), 60000)
    return url
  }

  return { evidence, loading, error, uploadFileEvidence, uploadLinkEvidence,
           uploadNoteEvidence, deleteEvidence, downloadEvidence, refetch: fetchEvidence }
}
